import { GameObject } from '../types';

/**
 * Game Objects Database
 * 
 * All measurements are in standard units:
 * - Length: meters (always the longest dimension - height, width, or depth)
 * - Weight: kilograms 
 * - Volume: cubic meters
 * 
 * Objects range from microscopic to astronomical scale to provide
 * interesting comparisons across all difficulty levels.
 * 
 * IMPORTANT: Before adding new objects, please review OBJECT_GUIDELINES.md
 * for standardization requirements, research guidelines, and quality standards.
 */

export const gameObjects: GameObject[] = [
  // SMALL EVERYDAY OBJECTS
  {
    id: 'grain_of_rice',
    name: 'grain_of_rice',
    displayName: 'Grain of Rice',
    measurements: {
      length: 0.005,      // 5mm
      weight: 0.000025,   // 25mg
      volume: 0.000000003 // 3 cubic mm
    },
    emoji: '🌾',
    description: 'A single grain of white rice',
    categories: ['food', 'tiny'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Rice',
      length: 'https://www.fao.org/3/y4751e/y4751e05.htm',
      weight: 'https://www.researchgate.net/publication/Rice_grain_characteristics'
    },
    facts: [
      'Rice feeds over half the world\'s population',
      'One rice plant can produce over 3,000 grains',
      'Rice is grown on every continent except Antarctica'
    ]
  },
  {
    id: 'paperclip',
    name: 'paperclip',
    displayName: 'Paperclip',
    measurements: {
      length: 0.05,       // 5cm
      weight: 0.0005,     // 0.5g
      volume: 0.00000006  // 0.06 cubic cm
    },
    emoji: '📎',
    description: 'Standard metal paperclip',
    categories: ['office', 'tools', 'tiny'],
    facts: [
      'Invented in 1899 by Johan Vaaler',
      'The modern design was patented in 1901',
      'Over 11 billion paperclips are sold annually'
    ]
  },
  {
    id: 'penny',
    name: 'penny',
    displayName: 'Penny',
    measurements: {
      length: 0.0195,     // 19.5mm diameter
      weight: 0.0025,     // 2.5g
      volume: 0.00000036  // 0.36 cubic cm
    },
    emoji: '🪙',
    description: 'US one cent coin',
    categories: ['household', 'tiny', 'historical'],
    facts: [
      'Made of zinc with copper plating since 1982',
      'Features Abraham Lincoln',
      'Costs more to make than it\'s worth'
    ]
  },
  {
    id: 'lightbulb',
    name: 'lightbulb',
    displayName: 'Incandescent Light Bulb',
    measurements: {
      length: 0.11,      // 11cm
      weight: 0.034,     // 34g
      volume: 0.0001     // ~100 cubic cm
    },
    emoji: '💡',
    description: 'Standard incandescent light bulb',
    categories: ['household', 'technology'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Incandescent_light_bulb'
    },
    facts: [
      'Invented in the 19th century (Thomas Edison is often credited)',
      'Less than 5% of energy becomes light, rest is heat',
      'Gradually being replaced by LEDs for efficiency'
    ]
  },
  {
    id: 'hammer',
    name: 'hammer',
    displayName: 'Hammer',
    measurements: {
      length: 0.33,      // 33cm
      weight: 1.0,       // 1kg
      volume: 0.0003     // ~300 cubic cm
    },
    emoji: '🔨',
    description: 'Standard claw hammer',
    categories: ['tools', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Hammer'
    },
    facts: [
      'One of the oldest tools, dating back to the Stone Age',
      'Used for driving nails and breaking objects',
      'Often has a claw for removing nails'
    ]
  },
  {
    id: 'stapler',
    name: 'stapler',
    displayName: 'Stapler',
    measurements: {
      length: 0.15,      // 15cm
      weight: 0.25,      // 250g
      volume: 0.0002     // 200 cubic cm
    },
    emoji: '📎',
    description: 'Office paper stapler',
    categories: ['office', 'tools'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Stapler'
    },
    facts: [
      'Binds papers together with metal staples',
      'Patented in 1866',
      'Some staplers can also be used to pin or tack papers to a board'
    ]
  },
  // SPORTS EQUIPMENT
  {
    id: 'soccer_ball',
    name: 'soccer_ball',
    displayName: 'Soccer Ball',
    measurements: {
      length: 0.22,      // 22cm diameter
      weight: 0.43,      // 430g (FIFA size 5 ball)
      volume: 0.0056     // ~5.6 liters (sphere volume)
    },
    emoji: '⚽',
    description: 'Standard football (soccer) ball',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Ball_(association_football)'
    },
    facts: [
      'Circumference about 70cm (size 5)',
      'Typically made of synthetic leather panels',
      'Used in the world’s most popular sport'
    ]
  },
  {
    id: 'golf_ball',
    name: 'golf_ball',
    displayName: 'Golf Ball',
    measurements: {
      length: 0.0427,    // 4.27cm diameter
      weight: 0.0459,    // 45.9g
      volume: 0.0000407  // 40.7 cubic cm
    },
    emoji: '🟡',
    description: 'Dimpled golf ball',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Golf_ball'
    },
    facts: [
      'Covered in dimples to improve aerodynamics',
      'Standardized diameter of 42.7mm',
      'Can reach speeds over 240 km/h when hit'
    ]
  },
  {
    id: 'hockey_puck',
    name: 'hockey_puck',
    displayName: 'Hockey Puck',
    measurements: {
      length: 0.076,     // 7.6cm diameter, 2.5cm thick
      weight: 0.17,      // 170g
      volume: 0.000076   // ~76 cubic cm (cylinder)
    },
    emoji: '🏒',
    description: 'Ice hockey puck (vulcanized rubber)',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Hockey_puck'
    },
    facts: [
      'Frozen before games to reduce bouncing',
      'Can be shot at over 160 km/h (100 mph)',
      'Standard black disk of vulcanized rubber'
    ]
  },
  {
    id: 'bowling_pin',
    name: 'bowling_pin',
    displayName: 'Bowling Pin',
    measurements: {
      length: 0.38,      // 38cm tall
      weight: 1.5,       // 1.5kg
      volume: 0.004      // ~4 liters (approx shape volume)
    },
    emoji: '🎳',
    description: 'Ten-pin bowling pin',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Bowling_pin'
    },
    facts: [
      'Made of hard maple wood coated with plastic',
      'Standard set of 10 pins in ten-pin bowling',
      'Regulation weight around 3 lb 6 oz'
    ]
  },
  // TECHNOLOGY GADGETS
  {
    id: 'laptop',
    name: 'laptop',
    displayName: 'Laptop Computer',
    measurements: {
      length: 0.36,      // 36cm (15-inch class laptop)
      weight: 2.0,       // 2kg
      volume: 0.0020     // 2 liters (approx volume)
    },
    emoji: '💻',
    description: 'Portable laptop computer',
    categories: ['technology', 'office'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Laptop'
    },
    facts: [
      'Powered by a rechargeable battery',
      'Modern laptops often have multi-core processors',
      'The first portable computers appeared in the 1980s'
    ]
  },
  {
    id: 'drone',
    name: 'drone',
    displayName: 'Quadcopter Drone',
    measurements: {
      length: 0.30,      // 30cm across
      weight: 1.5,       // 1.5kg
      volume: 0.010      // ~10 liters (with rotor span)
    },
    emoji: '🛸',
    description: 'Consumer camera drone',
    categories: ['technology', 'outdoor'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Quadcopter'
    },
    facts: [
      'Controlled remotely or autonomously',
      'Uses four rotors for stability and lift',
      'Often equipped with a camera for aerial photography'
    ]
  },
  {
    id: 'camera',
    name: 'camera',
    displayName: 'Digital Camera',
    measurements: {
      length: 0.15,      // 15cm width
      weight: 0.8,       // 800g
      volume: 0.0009     // ~900 cubic cm
    },
    emoji: '📷',
    description: 'Digital SLR camera',
    categories: ['technology', 'office'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Digital_camera'
    },
    facts: [
      'Captures images via a digital sensor',
      'Modern DSLRs can have 30+ megapixel resolution',
      'The first digital camera was invented in 1975'
    ]
  },
  {
    id: 'microscope',
    name: 'microscope',
    displayName: 'Laboratory Microscope',
    measurements: {
      length: 0.30,      // 30cm tall
      weight: 5.0,       // 5kg
      volume: 0.004      // ~4 liters
    },
    emoji: '🔬',
    description: 'Optical microscope for scientific use',
    categories: ['scientific', 'technology'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Microscope'
    },
    facts: [
      'Uses glass lenses to magnify tiny objects',
      'Invented in the 17th century',
      'Can magnify up to 1000x or more'
    ]
  },
  {
    id: 'smartwatch',
    name: 'smartwatch',
    displayName: 'Smartwatch',
    measurements: {
      length: 0.04,      // 4cm diameter face
      weight: 0.05,      // 50g
      volume: 0.000002   // 2 cubic cm (electronics only)
    },
    emoji: '⌚',
    description: 'Wearable smart watch',
    categories: ['technology', 'tiny'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Smartwatch'
    },
    facts: [
      'Can track health metrics like heart rate',
      'Connects to smartphones via Bluetooth',
      'First modern smartwatches introduced in the 2010s'
    ]
  },
  // MEDIUM OBJECTS
  {
    id: 'basketball',
    name: 'basketball',
    displayName: 'Basketball',
    measurements: {
      length: 0.24,       // 24cm diameter (NBA regulation 23.8-24.3cm)
      weight: 0.62,       // 620g (NBA regulation 567-650g)
      volume: 0.0072      // 7.2 liters (calculated sphere volume)
    },
    emoji: '🏀',
    description: 'Official size basketball',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Basketball_(ball)',
      length: 'https://official.nba.com/rule-no-1-court-dimensions-equipment/',
      weight: 'https://official.nba.com/rule-no-1-court-dimensions-equipment/'
    },
    facts: [
      'Circumference is about 75cm',
      'Must bounce between 49-54 inches when dropped from 6 feet',
      'Originally played with peach baskets'
    ]
  },
  {
    id: 'bowling_ball',
    name: 'bowling_ball',
    displayName: 'Bowling Ball',
    measurements: {
      length: 0.22,       // 22cm diameter (8.5 inches)
      weight: 6.4,        // 6.4kg (14 lbs average)
      volume: 0.0056      // 5.6 liters (calculated sphere volume)
    },
    emoji: '🎳',
    description: 'Standard bowling ball',
    categories: ['sports'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Bowling_ball',
      length: 'https://www.bowl.com/Equipment/Bowling_Balls/',
      weight: 'https://www.usbc.org/equipment/specifications'
    },
    facts: [
      'Maximum weight allowed is 16 pounds',
      'Made of polyurethane',
      'Has finger holes for grip'
    ]
  },
  {
    id: 'microwave',
    name: 'microwave',
    displayName: 'Microwave Oven',
    measurements: {
      length: 0.5,        // 50cm wide
      weight: 15,         // 15kg
      volume: 0.042       // 42 liters
    },
    emoji: '🔄',
    description: 'Countertop microwave oven',
    categories: ['household', 'kitchen', 'technology'],
    facts: [
      'Invented by accident in 1945',
      'Uses electromagnetic radiation at 2.45 GHz',
      'Heats food from the inside out'
    ]
  },
  {
    id: 'washing_machine',
    name: 'washing_machine',
    displayName: 'Washing Machine',
    measurements: {
      length: 0.6,        // 60cm wide
      weight: 70,         // 70kg
      volume: 0.21        // 210 liters
    },
    emoji: '🧺',
    description: 'Standard front-loading washing machine',
    categories: ['household', 'kitchen', 'technology'],
    facts: [
      'Can spin at up to 1,600 RPM',
      'Uses about 50 liters of water per load',
      'Saves 40 hours per week compared to hand washing'
    ]
  },
  {
    id: 'refrigerator',
    name: 'refrigerator',
    displayName: 'Refrigerator',
    measurements: {
      length: 1.7,        // 1.7m tall
      weight: 125,        // 125kg
      volume: 0.6         // 600 liters
    },
    emoji: '❄️',
    description: 'Standard kitchen refrigerator',
    categories: ['household', 'kitchen'],
    facts: [
      'Keeps food at 1-4°C (34-39°F)',
      'Uses refrigerant to remove heat',
      'Can last 15-20 years with proper maintenance'
    ]
  },
  {
    id: 'vacuum_cleaner',
    name: 'vacuum_cleaner',
    displayName: 'Vacuum Cleaner',
    measurements: {
      length: 1.2,       // 1.2m tall (upright)
      weight: 8.0,       // 8kg
      volume: 0.1        // 100 liters (bag capacity & body)
    },
    emoji: '🧹',
    description: 'Upright household vacuum cleaner',
    categories: ['household', 'technology'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Vacuum_cleaner'
    },
    facts: [
      'Invented in 1901 by Hubert Cecil Booth',
      'Uses suction to remove dust and debris from floors',
      'Modern vacuums often use HEPA filters to trap fine particles'
    ]
  },
  {
    id: 'human',
    name: 'human',
    displayName: 'Adult Human',
    measurements: {
      length: 1.70,      // 1.7m (average height)
      weight: 70.0,      // 70kg (average adult)
      volume: 0.066      // ~66 liters (human body volume)
    },
    emoji: '🧍',
    description: 'Average adult person',
    categories: ['animals'],
    facts: [
      'The human body is about 60% water',
      'Contains 206 bones',
      'Average lifespan globally is around 72 years'
    ]
  },
  {
    id: 'chair',
    name: 'chair',
    displayName: 'Wooden Chair',
    measurements: {
      length: 0.90,      // 90cm tall
      weight: 5.0,       // 5kg
      volume: 0.35       // ~350 liters
    },
    emoji: '🪑',
    description: 'Standard dining chair',
    categories: ['furniture', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Chair'
    },
    facts: [
      'Typically designed to seat one person',
      'Four-legged seat with a backrest',
      'An ancient furniture form - chairs have been used for millennia'
    ]
  },
  {
    id: 'dining_table',
    name: 'dining_table',
    displayName: 'Dining Table',
    measurements: {
      length: 1.50,      // 1.5m long
      weight: 30.0,      // 30kg
      volume: 0.30       // ~300 liters
    },
    emoji: '🍽️',
    description: 'Wooden dining table seating 4-6',
    categories: ['furniture', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Table_(furniture)'
    },
    facts: [
      'Often the central piece of a dining room',
      'Standard height around 75cm',
      'Early tables date back to at least the 3rd millennium BC'
    ]
  },
  {
    id: 'ladder',
    name: 'ladder',
    displayName: 'Step Ladder',
    measurements: {
      length: 1.80,      // 1.8m tall (6ft ladder)
      weight: 10.0,      // 10kg
      volume: 0.15       // ~150 liters
    },
    emoji: '🪜',
    description: 'Folding step ladder',
    categories: ['tools', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Ladder'
    },
    facts: [
      'Provides a portable means to reach higher places',
      'Modern ladders are often made of aluminum or fiberglass',
      'Must meet safety standards to support weight'
    ]
  },
  {
    id: 'lawn_mower',
    name: 'lawn_mower',
    displayName: 'Lawn Mower',
    measurements: {
      length: 1.50,      // 1.5m long (push mower)
      weight: 30.0,      // 30kg
      volume: 0.250      // 250 liters
    },
    emoji: '🌿',
    description: 'Gas-powered push lawn mower',
    categories: ['tools', 'outdoor'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Lawn_mower'
    },
    facts: [
      'Invented in 1830 in England to cut grass for sports grounds',
      'Uses a rotating blade to cut grass to an even height',
      'Modern mowers have engines or are electrically powered'
    ]
  },
  {
    id: 'chainsaw',
    name: 'chainsaw',
    displayName: 'Chainsaw',
    measurements: {
      length: 0.90,      // 90cm long
      weight: 5.0,       // 5kg
      volume: 0.03       // ~30 liters
    },
    emoji: '🪚',
    description: 'Gasoline-powered chainsaw',
    categories: ['tools', 'outdoor'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Chainsaw'
    },
    facts: [
      'Invented in the 1920s for logging',
      'Uses a rotating chain with teeth to cut wood',
      'Requires both hands to operate safely'
    ]
  },
  // LARGE OBJECTS
  {
    id: 'piano',
    name: 'piano',
    displayName: 'Piano',
    measurements: {
      length: 2.0,        // 2m long (grand piano)
      weight: 300,        // 300kg
      volume: 1.5         // 1.5 cubic meters
    },
    emoji: '🎹',
    description: 'Grand piano',
    categories: ['musical', 'furniture'],
    facts: [
      'Has about 220 strings',
      'Takes 3-4 people to move',
      'Has 88 keys (52 white, 36 black)'
    ]
  },
  {
    id: 'car',
    name: 'car',
    displayName: 'Car',
    measurements: {
      length: 4.5,        // 4.5m long (average sedan)
      weight: 1400,       // 1400kg (average sedan)
      volume: 3.0         // 3 cubic meters interior space
    },
    emoji: '🚗',
    description: 'Average sedan car',
    categories: ['vehicles'],
    lengthDimension: 'front-to-back length',
    sources: {
      general: 'https://en.wikipedia.org/wiki/Car',
      length: 'https://www.edmunds.com/car-reviews/',
      weight: 'https://www.fueleconomy.gov/feg/findacar.shtml'
    },
    facts: [
      'Average car has about 100 cubic feet of interior space',
      'Modern cars have over 30,000 parts',
      'The average car is parked 95% of the time'
    ]
  },
  {
    id: 'elephant',
    name: 'elephant',
    displayName: 'Elephant',
    measurements: {
      length: 6.0,        // 6m long
      weight: 6000,       // 6 tons
      volume: 5.5         // 5.5 cubic meters
    },
    emoji: '🐘',
    description: 'African elephant - largest land animal',
    categories: ['animals', 'nature'],
    lengthDimension: 'body length',
    facts: [
      'Can weigh up to 6 tons',
      'They have excellent memories',
      'Can live up to 70 years in the wild'
    ]
  },
  {
    id: 'giraffe',
    name: 'giraffe',
    displayName: 'Giraffe',
    measurements: {
      length: 5.5,        // 5.5m tall
      weight: 1200,       // 1.2 tons
      volume: 3.2         // 3.2 cubic meters
    },
    emoji: '🦒',
    description: 'Tallest land animal',
    categories: ['animals', 'nature'],
    lengthDimension: 'height',
    facts: [
      'Heart weighs 25 pounds',
      'Tongue is 18-20 inches long',
      'Only sleeps 5-30 minutes at a time'
    ]
  },
  {
    id: 'school_bus',
    name: 'school_bus',
    displayName: 'School Bus',
    measurements: {
      length: 12.0,       // 12m long
      weight: 11000,      // 11 tons
      volume: 25.0        // 25 cubic meters interior
    },
    emoji: '🚌',
    description: 'Typical American school bus',
    categories: ['vehicles'],
    facts: [
      'Can carry up to 90 children',
      'Painted in safety yellow',
      'Has flashing lights and stop signs for safety'
    ]
  },
  {
    id: 'bed',
    name: 'bed',
    displayName: 'Queen Bed',
    measurements: {
      length: 2.0,       // 2m long
      weight: 50,        // ~50kg (mattress + frame)
      volume: 0.9        // ~0.9 cubic m (mattress volume)
    },
    emoji: '🛏️',
    description: 'Queen-size bed',
    categories: ['furniture', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Bed'
    },
    facts: [
      'Standard queen mattress is 60 x 80 inches',
      'Humans spend about one-third of their life sleeping',
      'Beds have existed since ancient times (raised frames were used in Egypt)'
    ]
  },
  {
    id: 'sofa',
    name: 'sofa',
    displayName: 'Sofa',
    measurements: {
      length: 2.0,       // 2m long (three-seat sofa)
      weight: 80,        // 80kg
      volume: 2.0        // ~2 cubic m
    },
    emoji: '🛋️',
    description: 'Three-seat couch/sofa',
    categories: ['furniture', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Sofa'
    },
    facts: [
      'Also called a couch',
      'Typically seats 3 people',
      'The longest sofa ever built was over 1,000 meters long'
    ]
  },
  {
    id: 'tent',
    name: 'tent',
    displayName: 'Camping Tent',
    measurements: {
      length: 2.0,       // 2m long (4-person tent footprint)
      weight: 5.0,       // 5kg
      volume: 3.0        // ~3 cubic m interior
    },
    emoji: '🏕️',
    description: '4-person camping tent',
    categories: ['outdoor', 'household'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Tent'
    },
    facts: [
      'Portable shelter made of fabric and poles',
      'Modern tents can be set up in minutes',
      'Used for camping, military, and disaster relief housing'
    ]
  },
  {
    id: 'horse',
    name: 'horse',
    displayName: 'Horse',
    measurements: {
      length: 2.4,       // 2.4m (average horse length, nose to tail)
      weight: 500,       // 500kg
      volume: 0.7        // ~0.7 cubic m
    },
    emoji: '🐎',
    description: 'Adult horse (average)',
    categories: ['animals'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Horse'
    },
    facts: [
      'Domesticated ~5,500 years ago',
      'Have a near-360° field of vision',
      'Can sleep both standing up and lying down'
    ]
  },
  {
    id: 'cow',
    name: 'cow',
    displayName: 'Cow (Cattle)',
    measurements: {
      length: 2.4,       // 2.4m long (excluding tail)
      weight: 600,       // 600kg (adult cow)
      volume: 0.9        // ~0.9 cubic m
    },
    emoji: '🐄',
    description: 'Domestic cow',
    categories: ['animals'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Cattle'
    },
    facts: [
      'Have four-chambered stomachs for digesting tough plants',
      'Can form close friendships with other cows, and get stressed when separated',
      'There are over 1 billion cattle in the world'
    ]
  },
  {
    id: 'polar_bear',
    name: 'polar_bear',
    displayName: 'Polar Bear',
    measurements: {
      length: 2.5,       // 2.5m long (large male)
      weight: 450,       // 450kg
      volume: 0.8        // ~0.8 cubic m
    },
    emoji: '🐻‍❄️',
    description: 'Polar bear (world’s largest bear)',
    categories: ['animals', 'nature'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Polar_bear'
    },
    facts: [
      'Adult males can weigh over 700kg',
      'Excellent swimmers, can swim for days',
      'Have black skin under their white fur'
    ]
  },
  {
    id: 'hippopotamus',
    name: 'hippopotamus',
    displayName: 'Hippopotamus',
    measurements: {
      length: 3.5,       // 3.5m long
      weight: 1500,      // 1,500kg
      volume: 2.5        // ~2.5 cubic m
    },
    emoji: '🦛',
    description: 'Common hippopotamus',
    categories: ['animals', 'nature'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Hippopotamus'
    },
    facts: [
      'Spends most of its time in water to stay cool',
      'Can open its mouth about 150 degrees to show large tusks',
      'Despite bulk, can run ~30 km/h on land'
    ]
  },
  {
    id: 'saguaro_cactus',
    name: 'saguaro_cactus',
    displayName: 'Saguaro Cactus',
    measurements: {
      length: 12.0,      // 12m tall (mature)
      weight: 0,         // varies (up to several tons with water)
      volume: 0          // N/A
    },
    emoji: '🌵',
    description: 'Tall cactus of the Sonoran Desert',
    categories: ['nature'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Saguaro'
    },
    facts: [
      'Can live over 150 years',
      'May weigh over 2 tons when filled with water',
      'State flower of Arizona (blossoms in spring)'
    ]
  },
  {
    id: 't_rex',
    name: 't_rex',
    displayName: 'Tyrannosaurus rex',
    measurements: {
      length: 12.0,      // 12m long
      weight: 8000,      // 8,000kg (approx 8 tons)
      volume: 6.0        // ~6 cubic m
    },
    emoji: '🦖',
    description: 'T. rex - large carnivorous dinosaur',
    categories: ['animals', 'historical', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Tyrannosaurus'
    },
    facts: [
      'Lived ~68-66 million years ago in the Late Cretaceous',
      'Had the strongest bite force of any land animal (up to ~35,000 N)',
      'Fossils suggest it could have been both predator and scavenger'
    ]
  },
  // VERY LARGE OBJECTS
  {
    id: 'shipping_container',
    name: 'shipping_container',
    displayName: 'Shipping Container',
    measurements: {
      length: 12.2,       // 12.2m long (40ft)
      weight: 3800,       // 3.8 tons (empty)
      volume: 67.7        // 67.7 cubic meters
    },
    emoji: '📦',
    description: '40-foot shipping container',
    categories: ['vehicles'],
    facts: [
      'Revolutionized global trade',
      'Can carry up to 26 tons of cargo',
      'Over 20 million containers in use worldwide'
    ]
  },
  {
    id: 'house',
    name: 'house',
    displayName: 'House',
    measurements: {
      length: 15.0,       // 15m long
      weight: 80000,      // 80 tons
      volume: 300         // 300 cubic meters interior
    },
    emoji: '🏠',
    description: 'Standard 3-bedroom suburban home (2,000 sq ft)',
    categories: ['buildings'],
    facts: [
      'Contains about 13,000 separate parts',
      'Uses about 16,000 board-feet of lumber',
      'Takes 6 months to build on average'
    ]
  },
  {
    id: 'blue_whale',
    name: 'blue_whale',
    displayName: 'Blue Whale',
    measurements: {
      length: 30.0,       // 30m long (adult average)
      weight: 150000,     // 150,000kg (150 tons average adult)
      volume: 120.0       // 120 cubic meters (estimated body volume)
    },
    emoji: '🐋',
    description: 'Largest animal ever known to exist',
    categories: ['animals', 'nature', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Blue_whale',
      length: 'https://www.noaa.gov/fisheries/species/blue-whale',
      weight: 'https://www.worldwildlife.org/species/blue-whale'
    },
    facts: [
      'Heart alone can weigh as much as a car',
      'Can live up to 90 years',
      'Their tongue can weigh as much as an elephant'
    ]
  },
  {
    id: 'jumbo_jet',
    name: 'jumbo_jet',
    displayName: 'Jumbo Jet',
    measurements: {
      length: 70.0,       // 70m long (Boeing 747)
      weight: 412000,     // 412 tons
      volume: 858         // 858 cubic meters
    },
    emoji: '✈️',
    description: 'Boeing 747 jumbo jet',
    categories: ['vehicles', 'massive'],
    facts: [
      'Can carry up to 416 passengers',
      'Cruises at 41,000 feet',
      'Burns about 4 liters of fuel per second'
    ]
  },
  {
    id: 'football_field',
    name: 'football_field',
    displayName: 'Football Field',
    measurements: {
      length: 100.0,      // 100m long
      weight: 0,          // Not applicable
      volume: 0           // Not applicable
    },
    emoji: '🏈',
    description: 'American football field length',
    categories: ['sports'],
    facts: [
      'Including end zones, it\'s 120 yards long',
      'The field is 53 yards wide',
      'Has yard markers every 5 yards'
    ]
  },
  {
    id: 'cruise_ship',
    name: 'cruise_ship',
    displayName: 'Cruise Ship',
    measurements: {
      length: 362.0,      // 362m long (largest ships like Symphony of the Seas)
      weight: 228000,     // 228,000 tons gross tonnage
      volume: 435000      // 435,000 cubic meters (gross tonnage volume)
    },
    emoji: '🚢',
    description: 'Large cruise ship',
    categories: ['vehicles', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Symphony_of_the_Seas',
      length: 'https://www.royalcaribbean.com/cruise-ships/symphony-of-the-seas',
      weight: 'https://www.cruisemapper.com/ships/Symphony-of-the-Seas-ship-Royal-Caribbean'
    },
    facts: [
      'Can carry over 6,000 passengers',
      'Has 18 decks',
      'Weighs more than 2,000 blue whales'
    ]
  },
  {
    id: 'swimming_pool',
    name: 'swimming_pool',
    displayName: 'Swimming Pool',
    measurements: {
      length: 50.0,       // 50m long (Olympic standard)
      weight: 2500000,    // 2.5 million kg (water weight only)
      volume: 2500.0      // 2500 cubic meters (water volume)
    },
    emoji: '🏊',
    description: 'Olympic-sized swimming pool',
    categories: ['sports', 'outdoor'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Olympic-size_swimming_pool',
      length: 'https://www.fina.org/swimming/rules',
      volume: 'https://www.fina.org/swimming/rules'
    },
    facts: [
      'Contains 2.5 million liters of water',
      '50 meters long, 25 meters wide',
      'Minimum depth of 2 meters'
    ]
  },
  // HISTORIC & LARGE VEHICLES
  {
    id: 'titanic',
    name: 'titanic',
    displayName: 'RMS Titanic',
    measurements: {
      length: 269.0,     // 269m long
      weight: 52310000,  // 52,310 tons (approx displacement)
      volume: 0          // N/A
    },
    emoji: '🚢',
    description: 'Titanic - famous ocean liner (1912)',
    categories: ['vehicles', 'historical'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/RMS_Titanic'
    },
    facts: [
      'Carried ~2,200 passengers and crew',
      'Sank on its maiden voyage in 1912 after hitting an iceberg',
      'Its wreck lies ~3,800m underwater in the North Atlantic'
    ]
  },
  {
    id: 'submarine',
    name: 'submarine',
    displayName: 'Nuclear Submarine',
    measurements: {
      length: 175.0,     // 175m (Typhoon-class approx)
      weight: 48000000,  // 48,000 tons (submerged displacement)
      volume: 0          // N/A
    },
    emoji: '🔱',
    description: 'Large nuclear-powered submarine',
    categories: ['vehicles'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Submarine'
    },
    facts: [
      'Can stay submerged for months at a time',
      'Typhoon-class subs are the largest ever built',
      'Armed with ballistic missiles and a crew of 160+'
    ]
  },
  // NATURAL WONDERS
  {
    id: 'brachiosaurus',
    name: 'brachiosaurus',
    displayName: 'Brachiosaurus',
    measurements: {
      length: 25.0,      // 25m long
      weight: 40000,     // 40,000kg (estimated)
      volume: 20.0       // ~20 cubic m body volume
    },
    emoji: '🦕',
    description: 'Long-necked Jurassic dinosaur',
    categories: ['animals', 'historical', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Brachiosaurus'
    },
    facts: [
      'Lived about 150 million years ago',
      'Name means "arm lizard" due to long front legs',
      'Could reach leaves high up in trees'
    ]
  },
  {
    id: 'giant_sequoia',
    name: 'giant_sequoia',
    displayName: 'Giant Sequoia Tree',
    measurements: {
      length: 85.0,      // 85m tall (General Sherman tree)
      weight: 1900000,   // ~1,900,000kg (mass of largest tree)
      volume: 1487       // 1,487 cubic m trunk volume (largest known)
    },
    emoji: '🌲',
    description: 'Giant sequoia (world’s largest tree by volume)',
    categories: ['nature', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Sequoiadendron_giganteum'
    },
    facts: [
      'Can live over 3,000 years',
      'The General Sherman tree is the largest known living single stem tree',
      'Native to California’s Sierra Nevada'
    ]
  },
  {
    id: 'oak_tree',
    name: 'oak_tree',
    displayName: 'Oak Tree',
    measurements: {
      length: 20.0,      // 20m tall mature oak
      weight: 0,         // varies (tens of tons)
      volume: 0          // N/A
    },
    emoji: '🌳',
    description: 'Large mature oak tree',
    categories: ['nature'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Oak'
    },
    facts: [
      'Can live for centuries',
      'Produces acorns as seeds',
      'Wood is dense and commonly used in furniture and barrels'
    ]
  },
  {
    id: 'mount_everest',
    name: 'mount_everest',
    displayName: 'Mount Everest',
    measurements: {
      length: 8848.0,    // 8,848m tall
      weight: 0,         // N/A
      volume: 0          // N/A
    },
    emoji: '🏔️',
    description: 'Tallest mountain above sea level',
    categories: ['nature', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Mount_Everest'
    },
    facts: [
      'Peak is on the border of Nepal and China',
      'First summited in 1953 by Tenzing Norgay and Edmund Hillary',
      'Continues to grow ~4mm taller each year due to tectonic uplift'
    ]
  },
  // FAMOUS STRUCTURES
  {
    id: 'great_pyramid',
    name: 'great_pyramid',
    displayName: 'Great Pyramid of Giza',
    measurements: {
      length: 146.0,     // 146m tall originally
      weight: 5900000000, // ~5.9e9 kg (est. 5.9 million tons)
      volume: 2600000    // ~2,600,000 cubic m
    },
    emoji: '🛕',
    description: 'Great Pyramid (Khufu) in Egypt',
    categories: ['buildings', 'historical', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Great_Pyramid_of_Giza'
    },
    facts: [
      'Oldest of the Seven Wonders of the Ancient World',
      'Built ~2560 BC as a tomb for Pharaoh Khufu',
      'Consists of ~2.3 million stone blocks'
    ]
  },
  {
    id: 'eiffel_tower',
    name: 'eiffel_tower',
    displayName: 'Eiffel Tower',
    measurements: {
      length: 324.0,     // 324m tall
      weight: 7300000,   // ~7,300,000kg (iron structure)
      volume: 0          // N/A
    },
    emoji: '🗼',
    description: 'Eiffel Tower in Paris, France',
    categories: ['buildings', 'historical'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Eiffel_Tower'
    },
    facts: [
      'Built in 1889 for the World’s Fair',
      'Was the tallest structure in the world until 1930',
      'Painted every 7 years to prevent rust'
    ]
  },
  {
    id: 'statue_of_liberty',
    name: 'statue_of_liberty',
    displayName: 'Statue of Liberty',
    measurements: {
      length: 93.0,      // 93m including pedestal
      weight: 225000,    // ~225,000kg (statue only)
      volume: 0          // N/A
    },
    emoji: '🗽',
    description: 'Statue of Liberty (New York)',
    categories: ['buildings', 'historical'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Statue_of_Liberty'
    },
    facts: [
      'A gift from France to the USA in 1886',
      'Made of copper sheets (green patina formed over time)',
      'Represents Libertas, Roman goddess of liberty'
    ]
  },
  {
    id: 'burj_khalifa',
    name: 'burj_khalifa',
    displayName: 'Burj Khalifa',
    measurements: {
      length: 828.0,     // 828m tall
      weight: 0,         // N/A
      volume: 0          // N/A
    },
    emoji: '🏢',
    description: 'Burj Khalifa skyscraper (Dubai)',
    categories: ['buildings', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Burj_Khalifa'
    },
    facts: [
      'World’s tallest building since 2010',
      '163 floors and an observation deck on the 148th floor',
      'Construction used 330,000 m³ of concrete and 55,000 tons of steel'
    ]
  },
  // TINY OBJECTS
  {
    id: 'ant',
    name: 'ant',
    displayName: 'Ant',
    measurements: {
      length: 0.005,      // 5mm
      weight: 0.000002,   // 2mg
      volume: 0.000000001 // 1 cubic mm
    },
    emoji: '🐜',
    description: 'Common worker ant',
    categories: ['animals', 'tiny'],
    facts: [
      'Can lift 10-50 times their body weight',
      'Live in colonies of up to 500,000',
      'Have been around for 130 million years'
    ]
  },
  {
    id: 'ladybug',
    name: 'ladybug',
    displayName: 'Ladybug',
    measurements: {
      length: 0.007,      // 7mm
      weight: 0.000015,   // 15mg
      volume: 0.000000004 // 4 cubic mm
    },
    emoji: '🐞',
    description: 'Small spotted beetle',
    categories: ['animals', 'tiny'],
    facts: [
      'Can eat up to 5,000 aphids in their lifetime',
      'Hibernate in groups under rocks',
      'Are considered good luck in many cultures'
    ]
  },
  {
    id: 'honeybee',
    name: 'honeybee',
    displayName: 'Honey Bee',
    measurements: {
      length: 0.012,     // 12mm
      weight: 0.0001,    // 100mg
      volume: 0.00000002 // ~20 cubic mm
    },
    emoji: '🐝',
    description: 'Worker honey bee',
    categories: ['animals', 'tiny'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Honey_bee'
    },
    facts: [
      'Bees beat their wings ~200 times per second',
      'Live in colonies with a queen, workers, and drones',
      'Crucial pollinators for many crops'
    ]
  },
  {
    id: 'dust_mite',
    name: 'dust_mite',
    displayName: 'Dust Mite',
    measurements: {
      length: 0.0003,    // 0.3mm
      weight: 0.00000005, // ~50 micrograms
      volume: 0.00000000005 // ~0.05 cubic mm
    },
    emoji: '🔬',
    description: 'Microscopic dust mite',
    categories: ['animals', 'tiny'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/House_dust_mite'
    },
    facts: [
      'Lives in household dust and feeds on skin flakes',
      'Invisible to the naked eye',
      'A common allergen for humans'
    ]
  },
  {
    id: 'bacterium',
    name: 'bacterium',
    displayName: 'Bacterium (E. coli)',
    measurements: {
      length: 0.000002,  // ~2 micrometers
      weight: 0.000000000001, // ~1e-12 kg (1 picogram)
      volume: 0.000000000000000001 // ~1e-18 cubic m
    },
    emoji: '🦠',
    description: 'Single E. coli bacterium',
    categories: ['tiny', 'scientific'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Escherichia_coli'
    },
    facts: [
      'Too small to see with a regular microscope',
      'E. coli lives in the human gut',
      'Reproduces by splitting in two (binary fission)'
    ]
  },
  {
    id: 'grain_of_sand',
    name: 'grain_of_sand',
    displayName: 'Grain of Sand',
    measurements: {
      length: 0.0005,    // 0.5mm
      weight: 0.0000002, // ~0.2mg (quartz sand grain)
      volume: 0.00000000007 // ~0.07 cubic mm
    },
    emoji: '🏖️',
    description: 'Single grain of sand',
    categories: ['nature', 'tiny'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Sand'
    },
    facts: [
      'Sand grains are tiny fragments of rock or coral',
      'Typical sand size is 0.05–2 mm in diameter',
      'It takes millions of grains to form a handful of sand'
    ]
  },
  {
    id: 'coronavirus',
    name: 'coronavirus',
    displayName: 'Coronavirus (SARS-CoV-2)',
    measurements: {
      length: 0.00000012, // ~120 nm diameter
      weight: 0.0000000000000000005, // ~5e-19 kg
      volume: 0.0000000000000000000005 // ~5e-22 cubic m
    },
    emoji: '🦠',
    description: 'Single coronavirus particle',
    categories: ['tiny', 'scientific'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/SARS-CoV-2'
    },
    facts: [
      'Has a spiky outer surface (giving it a “corona” appearance)',
      'Causes COVID-19 disease',
      'About 100 billion could fit in a grain of salt'
    ]
  },
  // SPACE/ASTRONOMICAL OBJECTS
  {
    id: 'space_station',
    name: 'space_station',
    displayName: 'Space Station',
    measurements: {
      length: 108.0,      // 108m long (ISS)
      weight: 420000,     // 420 tons
      volume: 916         // 916 cubic meters
    },
    emoji: '🛰️',
    description: 'International Space Station',
    categories: ['space', 'technology'],
    facts: [
      'Orbits Earth every 93 minutes',
      'Travels at 17,500 mph',
      'Has been continuously occupied since 2000'
    ]
  },
  {
    id: 'saturn_v',
    name: 'saturn_v',
    displayName: 'Saturn V Rocket',
    measurements: {
      length: 110.6,     // 110.6m tall
      weight: 2970000,   // 2,970,000kg (fully fueled launch mass)
      volume: 8500       // ~8500 cubic m (approx external volume)
    },
    emoji: '🚀',
    description: 'NASA Saturn V moon rocket',
    categories: ['space', 'historical', 'vehicles'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Saturn_V'
    },
    facts: [
      'Launched Apollo astronauts to the Moon',
      'Tallest and most powerful rocket ever flown successfully',
      'First launched in 1967, it stood 363 feet tall'
    ]
  },
  {
    id: 'hubble_telescope',
    name: 'hubble_telescope',
    displayName: 'Hubble Space Telescope',
    measurements: {
      length: 13.2,      // 13.2m
      weight: 11110,     // 11,110kg
      volume: 0          // N/A (mostly open space)
    },
    emoji: '🔭',
    description: 'Hubble Space Telescope (in Earth orbit)',
    categories: ['space', 'scientific'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Hubble_Space_Telescope'
    },
    facts: [
      'Orbits ~547 km above Earth',
      'Launched in 1990 by Space Shuttle',
      'Provided deep views of the universe including the famous Hubble Deep Field'
    ]
  },
  {
    id: 'mars',
    name: 'mars',
    displayName: 'Mars (Planet)',
    measurements: {
      length: 6779000,   // ~6,779 km diameter
      weight: 641000000000000000000000, // 6.41e23 kg
      volume: 163000000000000000000,   // 1.63e20 cubic m
    },
    emoji: '🔴',
    description: 'Mars - the Red Planet',
    categories: ['space', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Mars'
    },
    facts: [
      'About half the diameter of Earth',
      'Has two small moons: Phobos and Deimos',
      'Home to the largest volcano in the solar system (Olympus Mons)'
    ]
  },
  {
    id: 'earth',
    name: 'earth',
    displayName: 'Earth',
    measurements: {
      length: 12742000,  // ~12,742 km diameter
      weight: 5972000000000000000000000, // 5.97e24 kg
      volume: 1083000000000000000000    // 1.083e21 cubic m
    },
    emoji: '🌎',
    description: 'Earth - our home planet',
    categories: ['space', 'massive', 'nature'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Earth'
    },
    facts: [
      'Only planet known to support life',
      'About 71% of its surface is covered in water',
      'Has one moon and a breathable atmosphere'
    ]
  },
  {
    id: 'moon',
    name: 'moon',
    displayName: 'Moon',
    measurements: {
      length: 3474000,   // 3,474 km diameter
      weight: 735000000000000000000000, // 7.35e22 kg
      volume: 21900000000000000000     // 2.19e19 cubic m
    },
    emoji: '🌕',
    description: 'Earth’s Moon',
    categories: ['space'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Moon'
    },
    facts: [
      'About one-quarter the diameter of Earth',
      'Has no atmosphere and low gravity (1/6 of Earth’s)',
      'Humans first visited in 1969 (Apollo 11)'
    ]
  },
  {
    id: 'sun',
    name: 'sun',
    displayName: 'Sun',
    measurements: {
      length: 1391000000, // 1.391 million km diameter
      weight: 1989000000000000000000000000000, // 1.989e30 kg
      volume: 1410000000000000000000000        // 1.41e27 cubic m
    },
    emoji: '☀️',
    description: 'The Sun (star at the center of our Solar System)',
    categories: ['space', 'massive'],
    sources: {
      general: 'https://en.wikipedia.org/wiki/Sun'
    },
    facts: [
      'Could fit about 1.3 million Earths inside its volume',
      'Composed mostly of hydrogen and helium',
      'Generates energy by nuclear fusion in its core'
    ]
  }
];


// Helper functions for object management
export const getObjectById = (id: string): GameObject | undefined => {
  return gameObjects.find(obj => obj.id === id);
};

export const getObjectsWithMeasurement = (measurementType: 'length' | 'weight' | 'volume'): GameObject[] => {
  return gameObjects.filter(obj => {
    const value = obj.measurements[measurementType];
    return value > 0;
  });
};

export const getObjectsByDifficulty = (_difficulty: 'easy' | 'medium' | 'hard'): GameObject[] => {
  // Return all objects since difficulty is now calculated dynamically
  return gameObjects;
};

export const getAllObjects = (): GameObject[] => {
  return gameObjects;
};

// Get objects by size category for easier testing/debugging
export const getObjectsBySize = () => {
  return {
    tiny: gameObjects.filter(obj => obj.measurements.length < 0.01),
    small: gameObjects.filter(obj => obj.measurements.length >= 0.01 && obj.measurements.length < 0.5),
    medium: gameObjects.filter(obj => obj.measurements.length >= 0.5 && obj.measurements.length < 5),
    large: gameObjects.filter(obj => obj.measurements.length >= 5 && obj.measurements.length < 50),
    huge: gameObjects.filter(obj => obj.measurements.length >= 50)
  };
};

// Get objects by category
export const getObjectsByCategory = (category: string): GameObject[] => {
  return gameObjects.filter(obj => 
    obj.categories && obj.categories.length > 0 && obj.categories.includes(category as any)
  );
};

// Get all available categories
export const getAvailableCategories = (): string[] => {
  const categories = new Set<string>();
  gameObjects.forEach(obj => {
    obj.categories?.forEach(cat => categories.add(cat));
  });
  return Array.from(categories).sort();
};

// Get category display info
export const getCategoryInfo = () => {
  return {
    household: { name: 'Household Items', emoji: '🏠', description: 'Common items found in homes' },
    food: { name: 'Food & Drinks', emoji: '🍎', description: 'Edible items and beverages' },
    animals: { name: 'Animals', emoji: '🐘', description: 'Living creatures from tiny to massive' },
    sports: { name: 'Sports Equipment', emoji: '⚽', description: 'Balls, equipment, and gear' },
    vehicles: { name: 'Vehicles', emoji: '🚗', description: 'Cars, planes, ships, and transport' },
    technology: { name: 'Technology', emoji: '📱', description: 'Electronics and gadgets' },
    nature: { name: 'Nature', emoji: '🌿', description: 'Natural objects and phenomena' },
    buildings: { name: 'Buildings', emoji: '🏢', description: 'Structures and architecture' },
    tools: { name: 'Tools', emoji: '🔧', description: 'Tools and instruments' },
    clothing: { name: 'Clothing', emoji: '👕', description: 'Clothing and accessories' },
    furniture: { name: 'Furniture', emoji: '🪑', description: 'Furniture and home items' },
    tiny: { name: 'Tiny Objects', emoji: '🔬', description: 'Very small objects' },
    massive: { name: 'Massive Objects', emoji: '🏔️', description: 'Very large objects' },
    space: { name: 'Space', emoji: '🚀', description: 'Space-related objects' },
    historical: { name: 'Historical', emoji: '🏛️', description: 'Historical artifacts' },
    musical: { name: 'Musical', emoji: '🎵', description: 'Musical instruments' },
    office: { name: 'Office', emoji: '📎', description: 'Office supplies and equipment' },
    kitchen: { name: 'Kitchen', emoji: '🍳', description: 'Kitchen items and appliances' },
    outdoor: { name: 'Outdoor', emoji: '🏕️', description: 'Garden, camping, outdoor items' },
    scientific: { name: 'Scientific', emoji: '🔬', description: 'Scientific instruments' }
  };
};

